import { processDayValue } from "./common";
import { TimesheetAnalysisData } from "presentation/utils/exportUtils/types";

 export const formatValue = (value: number) : number => {
    return Number.isInteger(value)
      ? Number(value.toString())
      : Number(value.toFixed(1));
 };

 export const formatEmployeeName = (fullName: string) => {
    const names = fullName.trim().split(" ");

    if (names.length <= 2) {
      return fullName;
    }

    return `${names[0]} ${names[names.length - 1]}`;
  };

export interface TimesheetAnalysisRow {
  name: string;
  formattedName: string;

  dailyValues: Map<number, DailyCell>;

  totalHours: number;
  vacationHours: number;
  absenceHours: number;
  availableHours: number;
  totalHoursAll: number;

  daysWorked: number;
  vacationDays: number;
  absenceDays: number;
  availableDays: number;
  totalDaysAll: number;

  percentWorked: number;
  percentAbsent: number;

  preventionsCount: number;
  displacementCount: number;
  extraHourCount: number;

  workHoursPerDay: number;
  sumRestHours: number;
}

export interface DailyCell {
  value: string | number;
  color: string;
}

const buildCell = (
  value: string | number,
  workHours: number,
): DailyCell => {

  const color =
    value === "F"
      ? "#E6E6FA"
      : value === "A"
      ? "#FAFAD2"
      : value === "H" ||
        value === "S" ||
        value === "D"
      ? "#C0C0C0"
      : value === 0 ||
        Number(value) < workHours
      ? "#FA8072"
      : "#FFFAFA";

  return {
    value,
    color,
  };
};

export const buildTimesheetAnalysisRow = (
  item: TimesheetAnalysisData,
  year: number,
  month: number,
): TimesheetAnalysisRow => {

  const daysInMonth =
    new Date(year, month, 0).getDate();

  const workHours =
    item.workHoursPerDay > 0
      ? item.workHoursPerDay
      : 8;

  let workDays = daysInMonth;

  const dailyValues =
    new Map<number, DailyCell>();

  for (
    let day = 1;
    day <= daysInMonth;
    day++
  ) {

    const hoursAllocated =
      item.daysAllocated.get(day) ?? 0;

    const hoursAbsent =
      item.daysAbscent.get(day) ?? 0;

    const hoursVacation =
      item.daysVacation.get(day) ?? 0;

    const hoursHoliday =
      item.daysHoliday.get(day) ?? 0;

    const hoursRest =
      item.daysRest.get(day) ?? 0;

    const {
      value,
      isWorkDay,
    } = processDayValue(
      day,
      year,
      month,
      hoursAllocated,
      hoursAbsent,
      hoursVacation,
      hoursHoliday,
      hoursRest,
      item.isSupportService,
    );

    if (!isWorkDay) {
      workDays--;
    }

    dailyValues.set(day, buildCell(value, workHours));
  }

  const totalHours =
    formatValue(item.sumAllocated + item.extraHourCount);

  const vacationHours =
    formatValue(item.sumVacation);

  const absenceHours =
    formatValue(item.sumAbsent);

  const availableHours = 
    formatValue(workDays * workHours - item.sumRestHours);

  const availableDays = 
    formatValue(availableHours / workHours);

  const totalHoursAll =
    formatValue(
      totalHours +
      vacationHours +
      absenceHours
    );

  const daysWorked =
    formatValue(totalHours / workHours);

  const vacationDays =
    formatValue(vacationHours / workHours);

  const absenceDays =
    formatValue(absenceHours / workHours);

  const totalDaysAll =
    formatValue(totalHoursAll / workHours);

  const workingDaysExcludingVacation = formatValue(workDays - vacationDays);

  const percentWorked =
  workingDaysExcludingVacation > 0
    ? formatValue(
        (
          daysWorked /
          workingDaysExcludingVacation
        ) * 100
      )
    : 0;

  const percentAbsent =
  workingDaysExcludingVacation > 0
    ? formatValue(
        (
          absenceDays /
          workingDaysExcludingVacation
        ) * 100
      )
    : 0;

  return {
    name: item.name,
    formattedName: formatEmployeeName(item.name),

    dailyValues,

    totalHours,
    vacationHours,
    absenceHours,
    availableHours,
    totalHoursAll,

    daysWorked,
    vacationDays,
    absenceDays,
    availableDays,
    totalDaysAll,

    percentWorked,
    percentAbsent,

    preventionsCount: item.preventionsCount,
    displacementCount: item.displacementCount,
    extraHourCount: item.extraHourCount,
    workHoursPerDay: workHours,
    sumRestHours: item.sumRestHours,
  };
};